import React from 'react';
import { useField, useFormikContext } from 'formik';
import {
  Autocomplete,
  Box,
  InputLabel,
  Paper,
  StandardTextFieldProps,
  styled,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FormikContext from './FormikContext';

interface iFormikField extends StandardTextFieldProps {
  name: string;
  label?: string;
  maxHeight?: number;
}

const FormikIndustry: React.FC<iFormikField> = ({
  name,
  label,
  maxHeight = 250,
  ...props
}) => {
  const { currentStep, steps, setSteps } = React.useContext(FormikContext);
  const [field, meta] = useField(name);

  const theme = useTheme();
  const medium = useMediaQuery(theme.breakpoints.up('md'));
  const space = medium ? 4 : 2;

  const { setFieldValue } = useFormikContext();

  const { touched, error, value } = meta;
  const { onBlur } = field;

  const options = INDUSTRIES.map((option) => {
    const firstLetter = option.title[0].toUpperCase();
    return {
      firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
      ...option,
    };
  });

  // Set steps hasError: true if field has error
  React.useEffect(() => {
    if (steps && steps.length > 0) {
      if (touched && error) {
        const _steps = [...steps];
        _steps[currentStep.step].hasError = true;
        setSteps(_steps);
        localStorage.setItem('steps', JSON.stringify(_steps));
      } else {
        const _steps = [...steps];
        _steps[currentStep.step].hasError = false;
        setSteps(_steps);
        localStorage.setItem('steps', JSON.stringify(_steps));
      }
    }
  }, [error]);

  return (
    <Box mt={label ? space : 0}>
      <InputLabel
        htmlFor={name}
        sx={{
          fontSize: 14,
          fontWeight: 500,
          color: 'text.primary',
          mb: 1,
        }}
      >
        {label}
      </InputLabel>
      <MuiFormikField
        id={name}
        options={options.sort(
          (a, b) => -b.firstLetter.localeCompare(a.firstLetter)
        )}
        groupBy={(op: any) => op.firstLetter}
        getOptionLabel={(op: any) => op.title}
        renderInput={(params) => (
          <TextField
            {...params}
            error={touched && Boolean(error)}
            helperText={touched && error}
            {...props}
          />
        )}
        renderGroup={(params) => (
          <div key={params.key}>
            <p className="label">{params.group}</p>
            <>{params.children}</>
          </div>
        )}
        PaperComponent={({ children }) => (
          <MuiGroupContainer>{children}</MuiGroupContainer>
        )}
        ListboxProps={{
          style: { maxHeight: maxHeight },
        }}
        fullWidth
        size="small"
        onChange={(event: any, newValue: any | null) => {
          setFieldValue(name, newValue?.title);
        }}
        value={options.find((op) => op.title === value)}
        onBlur={onBlur}
      />
    </Box>
  );
};

export default FormikIndustry;

const MuiFormikField = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: 14,
    fontWeight: 500,
    '& fieldset': {
      borderColor: theme.palette.grey[300],
      boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.05)',
      borderWidth: 1,
      borderRadius: 8,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.grey[400],
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.grey[500],
      borderWidth: 1,
    },
  },

  '& .MuiFormHelperText-root': {
    color: theme.palette.error.main,
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: 1,
    marginLeft: 0,
  },
}));

const MuiGroupContainer = styled(Paper)(({ theme }) => ({
  boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.05)',
  border: `1px solid ${theme.palette.grey[300]}`,

  '& .MuiAutocomplete-groupLabel': {
    backgroundColor: 'transparent',
    padding: 0,
    margin: 0,
  },

  '& .MuiAutocomplete-groupUl': {
    padding: 0,
    margin: 0,
  },

  '& .label': {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 1,
    color: theme.palette.grey[900],
    textTransform: 'uppercase',
    padding: theme.spacing(1),
    margin: 0,
  },

  '& .MuiAutocomplete-option': {
    padding: theme.spacing(0.65, 2),
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: 1,
    color: theme.palette.grey[800],
    textTransform: 'capitalize',

    '&:hover': {
      backgroundColor: '#eae9fa',
    },
  },

  '& .MuiAutocomplete-option[data-focus="true"]': {
    backgroundColor: 'red',
  },
}));

const INDUSTRIES = [
  { title: 'Software' },
  { title: 'Clothing & Accessories' },
  { title: 'Consulting Services' },
  { title: 'Convenience Store' },
  { title: 'Beauty Products' },
  { title: 'Home Goods & Furniture' },
  { title: 'Home Electronics' },
  { title: 'Auto Parts & Accessories' },
  { title: 'Jewelry Stores, Watches, Clocks, & Silverware Stores' },
  { title: 'Precious Stones & Metals' },
  { title: 'Other Merchandise' },
  { title: 'Software as a Service' },
  { title: 'Apps' },
  { title: 'Books' },
  { title: 'Music or Other Media' },
  { title: 'Games' },
  { title: 'Blogs and Written Content' },
  { title: 'Other Digital Goods' },
  { title: 'Restaurants and nightlife' },
  { title: 'Grocery stores' },
  { title: 'Caterers' },
  { title: 'Other food and dining' },
  { title: 'Consulting' },
  { title: 'Printing and publishing' },
  { title: 'Attorneys and lawyers' },
  { title: 'Bankruptcy services' },
  { title: 'Bail Bonds' },
  { title: 'Accounting, auditing, or tax prep' },
  { title: 'Computer repair' },
  { title: 'Testing laboratories' },
  { title: 'Auto services' },
  { title: 'Car rentals' },
  { title: 'Car sales' },
  { title: 'Lead generation' },
  { title: 'Direct marketing' },
  { title: 'Utilities' },
  { title: 'Government services' },
  { title: 'Telemarketing' },
  { title: 'Credit counseling or credit repair' },
  { title: 'Mortgage consulting services' },
  { title: 'Debt reduction services' },
  { title: 'Warranty services' },
  { title: 'Other Marketing Services' },
  { title: 'Other Business Services' },
  { title: 'Civic, fraternal, or political organizations' },
  { title: 'Charities or social service organizations' },
  { title: 'Religious organizations' },
  { title: 'Country clubs' },
  { title: 'Political organizations' },
  { title: 'Other Membership Organizations' },
  { title: 'Photography studios' },
  { title: 'Health and beauty spas' },
  { title: 'Salons and barbers' },
  { title: 'Landscaping services' },
  { title: 'Massage parlors' },
  { title: 'Counseling services' },
  { title: 'Health and wellness coaching' },
  { title: 'Laundry or cleaning services' },
  { title: 'Dating services' },
  { title: 'Other Personal Services' },
  { title: 'Ridesharing' },
  { title: 'Taxis and limos' },
  { title: 'Courier services' },
  { title: 'Parking lots' },
  { title: 'Travel agencies' },
  { title: 'Freight forwarders' },
  { title: 'Shipping or forwarding' },
  { title: 'Commuter Transportation' },
  { title: 'Cruise lines' },
  { title: 'Airlines and air carriers' },
  { title: 'Other Transportation Services' },
  { title: 'Property rentals' },
  { title: 'Hotels, inns, or motels' },
  { title: 'Timeshares' },
  { title: 'Other Travel and Lodging' },
  { title: 'Medical devices' },
  { title: 'Doctors and physicians' },
  { title: 'Opticians and eyeglasses' },
  { title: 'Dentists and orthodontists' },
  { title: 'Chiropractors' },
  { title: 'Nursing or personal care facilities' },
  { title: 'Hospitals' },
  { title: 'Personal fundraising or crowdfunding' },
  { title: 'Mental health services' },
  { title: 'Assisted Living' },
  { title: 'Veterinary services' },
  { title: 'Medical organizations' },
  { title: 'Telemedicine and Telehealth' },
  { title: 'Other Medical Services' },
  { title: 'Child care services' },
  { title: 'Colleges or universities' },
  { title: 'Vocational schools or trade schools' },
  { title: 'Elementary or secondary schools' },
  { title: 'Other Educational Services' },
  { title: 'Event Ticketing' },
  { title: 'Tourist attractions' },
  { title: 'Recreational camps' },
  { title: 'Musicians, bands, or orchestras' },
  { title: 'Amusement parks, carnivals, or circuses' },
  { title: 'Fortune tellers' },
  { title: 'Movie theaters' },
  { title: 'Betting or fantasy sports' },
  { title: 'Lotteries' },
  { title: 'Sports forecasts or prediction services' },
  { title: 'Online gambling' },
  { title: 'Other Entertainment and Recreation' },
  { title: 'General contractors' },
  { title: 'Electrical contractors' },
  { title: 'Carpentry contractors' },
  { title: 'Special trade contractors' },
  { title: 'Telecom services' },
  { title: 'Telecom equipment' },
  { title: 'A/C and heating contractors' },
  { title: 'Other Building Services' },
  { title: 'Insurance' },
  { title: 'Securities brokers or dealers' },
  { title: 'Money orders' },
  { title: 'Currency exchanges' },
  { title: 'Wire transfers' },
  { title: 'Check cashing' },
  { title: 'Loans or lending' },
  { title: 'Collection agencies' },
  { title: 'Money services or transmission' },
  { title: 'Investment services' },
  { title: 'Virtual currencies' },
  { title: 'Digital wallets' },
  { title: 'Cryptocurrencies' },
  { title: 'Other Financial Services' },
  { title: 'Pharmacies or pharmaceuticals' },
  { title: 'Tobacco or cigars' },
  { title: 'Adult content or services' },
  { title: 'Vapes, e-cigarettes, e-juice, or related products' },
  { title: 'Weapons or munitions' },
  { title: 'Supplements or nutraceuticals' },
  { title: 'Marijuana dispensaries' },
  { title: 'Marijuana-related products' },
  { title: 'Accessories for tobacco or marijuana' },
  { title: 'Alcohol' },
];
